import React, { useEffect, useRef, useState } from 'react'
import { Autocomplete, Box, Button, Chip, MenuItem, Popover, Select, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import { C } from '../RestaurantDashboard/tokens'
import RangePicker from '../RestaurantDashboard/RangePicker'
import { rangeLabel } from '../RestaurantDashboard/format'
import { CHANNEL_OPTIONS, COMPARE_OPTIONS, FULFILLMENT_OPTIONS, PRESETS } from './saUtil'

const selectSx = {
  height: 36,
  fontSize: 14,
  bgcolor: '#fff',
  borderRadius: '8px',
  '.MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.borderStrong },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.brandText, borderWidth: 1.5 }
}

const segSx = {
  bgcolor: '#fff',
  flexWrap: 'wrap',
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontSize: 13.5,
    fontWeight: 500,
    color: C.text,
    borderColor: C.border,
    px: 1.5,
    height: 36,
    '&.Mui-selected': { bgcolor: C.text, color: '#fff', '&:hover': { bgcolor: '#1F2937' } },
    '&.Mui-focusVisible': { outline: `2px solid ${C.brandText}`, outlineOffset: 2 }
  }
}

/**
 * Sticky filter bar. One filter drives every section of the page. White; a
 * border and soft shadow appear only once it is pinned (sentinel observer).
 */
export default function SaFilterBar({ filter, today, restaurants, onChange, onExport, exporting, exportDisabled }) {
  const customRef = useRef(null)
  const sentinelRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [stuck, setStuck] = useState(false)

  useEffect(() => {
    const el = sentinelRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return undefined
    const top = window.matchMedia('(min-width:600px)').matches ? 0 : 56
    const io = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting), { rootMargin: `-${top + 1}px 0px 0px 0px`, threshold: 0 })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const set = patch => onChange({ ...filter, ...patch })
  const selected = restaurants.filter(r => filter.restaurantIds.includes(r.id))

  return (
    <>
      <Box ref={sentinelRef} aria-hidden sx={{ height: '1px', mb: '-1px' }} />
      <Box
        role="toolbar"
        aria-label="Dashboard filters"
        sx={{
          position: 'sticky',
          top: { xs: 56, sm: 0 },
          zIndex: 6,
          bgcolor: '#fff',
          borderBottom: `1px solid ${stuck ? C.border : 'transparent'}`,
          boxShadow: stuck ? '0 2px 8px rgba(17,24,39,0.06)' : 'none',
          transition: 'box-shadow 160ms ease, border-color 160ms ease',
          py: 1.25,
          mb: 3
        }}>
        <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 4 }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
          <ToggleButtonGroup exclusive size="small" value={filter.preset} aria-label="Date range" sx={segSx}
            onChange={(_, v) => { if (v === 'custom') setOpen(true); else if (v) set({ preset: v }) }}>
            {PRESETS.map(p => p.key === 'custom' ? (
              <ToggleButton key={p.key} value="custom" ref={customRef} aria-label="Custom date range" aria-haspopup="dialog" onClick={() => setOpen(true)}>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 15, mr: 0.75 }} />
                {filter.preset === 'custom' ? rangeLabel(filter.from, filter.to) : 'Custom'}
              </ToggleButton>
            ) : (
              <ToggleButton key={p.key} value={p.key} aria-label={p.label}>{p.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Popover open={open} anchorEl={customRef.current} onClose={() => setOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { mt: 1, borderRadius: '12px', border: `1px solid ${C.border}`, boxShadow: '0 12px 32px rgba(17,24,39,0.12)' } }}>
            <RangePicker from={filter.from || today} to={filter.to || today} max={today}
              onChange={(a, b) => { setOpen(false); set({ preset: 'custom', from: a, to: b }) }} />
          </Popover>

          <Select size="small" value={filter.compare} onChange={e => set({ compare: e.target.value })} sx={selectSx}
            disabled={filter.preset === 'all'} inputProps={{ 'aria-label': 'Comparison' }}>
            {COMPARE_OPTIONS.map(o => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
          </Select>

          <Autocomplete
            multiple
            size="small"
            options={restaurants}
            value={selected}
            onChange={(_, v) => set({ restaurantIds: v.map(r => r.id) })}
            getOptionLabel={o => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            limitTags={1}
            disableCloseOnSelect
            renderTags={(value, getTagProps) => value.map((o, i) => <Chip size="small" label={o.name} {...getTagProps({ index: i })} key={o.id} />)}
            renderInput={params => (
              <TextField {...params} placeholder={selected.length ? '' : 'All restaurants'} inputProps={{ ...params.inputProps, 'aria-label': 'Restaurants' }}
                sx={{ '& .MuiOutlinedInput-root': { ...selectSx, height: 'auto', minHeight: 36, py: '2px !important' } }} />
            )}
            sx={{ minWidth: 220, maxWidth: 320 }}
          />

          <Select size="small" value={filter.channel} onChange={e => set({ channel: e.target.value })} sx={selectSx} inputProps={{ 'aria-label': 'Channel' }}>
            {CHANNEL_OPTIONS.map(o => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
          </Select>

          <Select size="small" value={filter.fulfillment} onChange={e => set({ fulfillment: e.target.value })} sx={selectSx} inputProps={{ 'aria-label': 'Fulfillment' }}>
            {FULFILLMENT_OPTIONS.map(o => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
          </Select>

          <Button variant="outlined" onClick={onExport} disabled={exportDisabled || exporting} startIcon={<FileDownloadOutlinedIcon />}
            sx={{
              ml: 'auto', height: 36, textTransform: 'none', fontSize: 14, fontWeight: 500, color: C.text, bgcolor: '#fff',
              borderColor: C.border, borderRadius: '8px', '&:hover': { borderColor: C.borderStrong, bgcolor: '#fff' },
              '&.Mui-focusVisible': { outline: `2px solid ${C.brandText}`, outlineOffset: 2 }
            }}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </Box>
      </Box>
    </>
  )
}
